import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Table } from 'antd'
import { isNil, join } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'
import { formatDateFromNow } from '../../../core/utils'

import { PAGINATION } from '../../../common/constants'

import { useLoggedInUser } from '../../../account/hooks'

import PriorityTaskDeleteButton from './priority-task-delete-button'
import PriorityTaskDoneButton from './priority-task-done-button'

const PriorityTasksTable = ({ isLoading, tasks, total }) => {
  const { settings: userSettings } = useLoggedInUser()

  const {
    currentPage = PAGINATION.DEFAULT_PAGE,
    pageSize = userSettings?.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    setQueryParams,
  } = useQueryParams()

  const tableColumns = useMemo(
    () => [
      {
        title: 'Task name',
        render: ({ id, name }) => <Link to={id}>{name}</Link>,
      },
      {
        title: 'Task description',
        dataIndex: 'description',
      },
      {
        title: 'Menu Tags',
        dataIndex: 'tags',
        render: tags => join(tags, ','),
      },
      {
        title: 'KPI',
        dataIndex: 'kpi',
      },
      {
        title: 'Required dish count',
        dataIndex: 'customRequiredDishCount',
      },
      {
        title: 'Task allocation type',
        dataIndex: 'allocationType',
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
      },
      {
        title: 'Status',
        render: ({ isDone }) => (!isDone ? 'Active' : 'Done'),
      },
      {
        title: 'Added',
        dataIndex: 'createdAt',
        render: date => date && formatDateFromNow(date),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: createdBy => createdBy?.name,
      },
      {
        title: 'Actions',
        render: ({ id, isDone }) => (
          <>
            <PriorityTaskDoneButton taskId={id} isDone={isDone} />
            <PriorityTaskDeleteButton taskId={id} />
          </>
        ),
      },
    ],
    [],
  )

  const handleChangePagination = useCallback(
    (page, size) =>
      setQueryParams({
        currentPage: size !== pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pageSize, setQueryParams],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(total)) {
      return false
    }

    return {
      current: Number(currentPage),
      pageSize,
      total,
      onChange: handleChangePagination,
      onShowSizeChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
    }
  }, [total, currentPage, pageSize, handleChangePagination])

  return (
    <Table
      loading={isLoading}
      rowKey="id"
      columns={tableColumns}
      dataSource={tasks}
      pagination={paginationConfig}
    />
  )
}

PriorityTasksTable.propTypes = {
  isLoading: PropTypes.bool,
  tasks: PropTypes.array,
  total: PropTypes.number,
}

PriorityTasksTable.defaultProps = {
  isLoading: false,
  tasks: [],
  total: 0,
}

export default PriorityTasksTable
